import { V4_API_PREFIX } from "./api.constants";
import { SPLOOT_BADGE_COLOR } from "../themes/badge";
import {
  EPartnerStatus,
  EPartnerRole,
  EPartnerLeaveStatus,
} from "../interfaces";

const { ACTIVE, ONBOARDING, ON_HOLD, BLOCKED, PENDING_VERIFICATION } =
  EPartnerStatus;
const { GROOMER, WALKER, TRAINER } = EPartnerRole;
const { APPLIED, APPROVED, REJECTED } = EPartnerLeaveStatus;

export enum EPartnerProfileTabId {
  OVERVIEW = "overview",
  WORKING_HOURS = "working-hours",
  TRAINING = "training",
  JOINING_FEE = "joining-fee",
  REVIEWS = "reviews",
  DOCUMENTS_AND_BANK = "documents-bank",
  ACTIVITY_LOG = "activity-log",
}

export enum EAssignmentModalMode {
  UPDATE = "update",
  ASSIGN = "assign",
}

export enum SLEEVE_LENGTH {
  HALF_SLEEVE = "HALF_SLEEVE",
  FULL_SLEEVE = "FULL_SLEEVE",
}

export const PARTNER_PROFILE_TAB_IDS: EPartnerProfileTabId[] = [
  EPartnerProfileTabId.OVERVIEW,
  EPartnerProfileTabId.WORKING_HOURS,
  EPartnerProfileTabId.TRAINING,
  EPartnerProfileTabId.JOINING_FEE,
  EPartnerProfileTabId.REVIEWS,
  EPartnerProfileTabId.DOCUMENTS_AND_BANK,
  EPartnerProfileTabId.ACTIVITY_LOG,
];

export const PARTNER_STATUS_BADGE_COLORS = {
  [ACTIVE]: SPLOOT_BADGE_COLOR.GREEN,
  [ONBOARDING]: SPLOOT_BADGE_COLOR.YELLOW,
  [ON_HOLD]: SPLOOT_BADGE_COLOR.ORANGE,
  [BLOCKED]: SPLOOT_BADGE_COLOR.RED,
  [PENDING_VERIFICATION]: SPLOOT_BADGE_COLOR.YELLOW,
} as const;

export const PARTNER_ONLINE_STATUS_BADGE_COLORS = {
  true: SPLOOT_BADGE_COLOR.GREEN,
  false: SPLOOT_BADGE_COLOR.NEUTRAL,
};

export const PARTNER_ROLE_BADGE_COLORS = {
  [GROOMER]: SPLOOT_BADGE_COLOR.PURPLE,
  [WALKER]: SPLOOT_BADGE_COLOR.BLUE,
  [TRAINER]: SPLOOT_BADGE_COLOR.ORANGE,
};

export const PARTNER_LEAVE_STATUS_BADGE_COLORS = {
  [APPLIED]: SPLOOT_BADGE_COLOR.ORANGE,
  [APPROVED]: SPLOOT_BADGE_COLOR.GREEN,
  [REJECTED]: SPLOOT_BADGE_COLOR.RED,
};

/**
 * Constants for marking partner order as paid
 */
export const MARK_PAID_REQUEST = {
  PAYMENT_REFERENCE: "Onboarding",
  PAYMENT_METHOD: "COD",
  NOTES: "Onboarding",
} as const;

export const PARTNER_ENDPOINTS = {
  GET_PARTNERS: `/${V4_API_PREFIX}/partners`,
  PARTNER_BY_ID: (partnerId: string) => `/${V4_API_PREFIX}/partners/${partnerId}`,
  RESET_ONBOARDING: (partnerId: string) =>
    `/${V4_API_PREFIX}/partners/${partnerId}/reset-onboarding`,
  PARTNER_LEAVES: (partnerId: string) => `/${V4_API_PREFIX}/partners/${partnerId}/leaves`,
  PARTNER_LEAVE_BY_ID: (partnerId: string, leaveId: string) =>
    `/${V4_API_PREFIX}/partners/${partnerId}/leaves/${leaveId}`,
  PARTNER_LEAVES_APPROVE: (partnerId: string) =>
    `/${V4_API_PREFIX}/partners/${partnerId}/leaves/approve`,
  PARTNER_LEAVES_REJECT: (partnerId: string) =>
    `/${V4_API_PREFIX}/partners/${partnerId}/leaves/reject`,
  PARTNER_LEAVE_APPROVE: (partnerId: string, leaveId: string) =>
    `/${V4_API_PREFIX}/partners/${partnerId}/leaves/${leaveId}/approve`,
  PARTNER_LEAVE_REJECT: (partnerId: string, leaveId: string) =>
    `/${V4_API_PREFIX}/partners/${partnerId}/leaves/${leaveId}/reject`,
  PARTNER_LEAVES_STATUS: (partnerId: string) =>
    `/${V4_API_PREFIX}/partners/${partnerId}/leaves/status`,
  PARTNER_REVIEWS: (partnerId: string) =>
    `/${V4_API_PREFIX}/partners/${partnerId}/reviews`,
  PARTNER_REVIEW_STATS: (partnerId: string) =>
    `/${V4_API_PREFIX}/partners/${partnerId}/reviews/stats`,
  PARTNER_PERFORMANCE: (partnerId: string) =>
    `/${V4_API_PREFIX}/partners/${partnerId}/performance`,
  PARTNER_WORKING_HOURS: (partnerId: string) =>
    `/${V4_API_PREFIX}/partners/${partnerId}/working-hours`,
  PARTNER_WORKING_HOUR_METADATA:
    `/${V4_API_PREFIX}/metadata/partner-app/partner-working-hour-metadata.json`,
  PARTNER_ONBOARDING: (partnerId: string) =>
    `/${V4_API_PREFIX}/partner-onboarding/${partnerId}`,
  /** GET admin details: flowVersion (e.g. 2026-03) and partnerId */
  PARTNER_ONBOARDING_ADMIN_DETAILS: (partnerId: string) =>
    `/${V4_API_PREFIX}/partner-onboarding/2026-03/${partnerId}/admin-details`,
  /** POST mark joining fee as paid (EXTERNAL vendor only) */
  MARK_JOINING_FEE_PAID: (partnerId: string) =>
    `/${V4_API_PREFIX}/partner-onboarding/2026-03/${partnerId}/joining-fees/mark-paid`,
  /** POST approve PAN (external KYC) */
  APPROVE_PAN: (partnerId: string) =>
    `/${V4_API_PREFIX}/partner-onboarding/2026-03/${partnerId}/kyc/approve-pan`,
  /** POST approve bank account (external KYC) */
  APPROVE_BANK: (partnerId: string) =>
    `/${V4_API_PREFIX}/partner-onboarding/2026-03/${partnerId}/kyc/approve-bank`,
  PARTNER_ORDER_KIT_BY_ORDER_ID: (orderId: string) =>
    `/${V4_API_PREFIX}/partner-orders/${orderId}`,
  MARK_ORDER_KIT_AS_PAID: (orderId: string) =>
    `/${V4_API_PREFIX}/partner-orders/${orderId}/mark-paid`,
  INITIATE_ONBOARDING: `/${V4_API_PREFIX}/partner-onboarding`,
  COMPLETE_PARTNER_TRAINING: (partnerId: string) =>
    `/${V4_API_PREFIX}/partner-onboarding/${partnerId}/stages/training/complete`,
  BULK_COMPLETE_TRAINING: `/${V4_API_PREFIX}/partner-onboarding/bulk/training/complete`,
  GET_SPECIFIC_STAGE_DATA: (partnerId: string, stage: string) =>
    `/${V4_API_PREFIX}/partner-onboarding/${partnerId}/stages/${stage}`,
  GET_DOCUMENT_PRESIGNED_URL: (partnerId: string) =>
    `/${V4_API_PREFIX}/partners/${partnerId}/documents/presigned-url`,
  GET_DOCUMENT_DOWNLOAD_URL: (partnerId: string, documentType: string) =>
    `/${V4_API_PREFIX}/partners/${partnerId}/documents/${documentType}/download-url`,
};

export const PARTNER_PROGRESS_TABS = [
  { id: "signup", label: "Sign Up", completed: true },
  { id: "training", label: "Training", completed: true },
  { id: "documents", label: "Documents", completed: true },
  { id: "kit", label: "Kit", completed: true },
  { id: "active", label: "Partner Active", completed: true },
];

/**
 * Partner onboarding progress bar (admin onboarding flow)
 * Order matters and must match stage-to-index mapping in progress utils.
 */
export const PARTNER_ONBOARDING_PROGRESS_STEP_LABELS = {
  onb_signup: "Sign Up",
  onb_work_settings: "Work Settings",
  onb_training: "Training",
  onb_joining_fee: "Joining Fee",
  onb_documents: "Doc Verification",
  onb_partner_active: "Partner Active",
} as const;

export type PartnerOnboardingProgressStepId =
  keyof typeof PARTNER_ONBOARDING_PROGRESS_STEP_LABELS;

export const PARTNER_ONBOARDING_PROGRESS_STEP_IDS = Object.keys(
  PARTNER_ONBOARDING_PROGRESS_STEP_LABELS,
) as PartnerOnboardingProgressStepId[];

// Filter Drawer Constants
export enum ONLINE_STATUS {
  ONLINE = "Online",
  OFFLINE = "Offline",
}

// Button labels
export const PARTNER_PROFILE_BUTTON_LABELS = {
  APPROVE_TRAINING: "Approve Training",
  APPROVE_EXPERIENCE: "Approve Experience",
  MARK_AS_ACTIVE: "Mark as Active",
} as const;

export const RATING_VALUES = [1, 2, 3, 4, 5] as const;
export const TOTAL_STARS = 5;
export const GEO_FETCH_LIMIT = 100;
export const DISABLED_INPUT_CLASSES = "opacity-50 cursor-not-allowed";

// Working Hours Constants
export const SLOT_STEP_MINUTES = 30;
export const TIME_INPUT_PLACEHOLDER = "00:00--";
export const BUTTON_LABELS = {
  ADD: "Add",
  CANCEL: "Cancel",
  UPDATE: "Update",
  UPDATING: "Updating...",
} as const;

export const SERVICE_FILTER_IDS = {
  ALL: "all-service-categories",
  GROOMING: "grooming",
  WALKING: "walking",
  TRAINING: "training",
} as const;

export const STATUS_FILTER_IDS = {
  ALL: "all-statuses",
  ACTIVE: "active",
  ONBOARDING: "onboarding",
  ON_HOLD: "on-hold",
  BLOCKED: "blocked",
} as const;

// Kit Tab Display Constants
export const KIT_DISPLAY_CONSTANTS = {
  FALLBACK_VALUE: "-",
  TITLE: "Kit Details",
  MESSAGES: {
    NO_KIT_ORDER: "No kit order data available",
    LOADING: "Loading kit order details...",
    STAGE_NOT_COMPLETED: "Kit order stage not completed",
    NO_ORDER_ID: "No order ID found in kit order data",
    NO_KIT_ORDER_FOUND: "No kit order found",
  },
  LABELS: {
    KIT_QUANTITY: "Kit Quantity",
    KIT_SIZE: "Kit Size",
    DELIVERY_ADDRESS: "Delivery Address",
    PAID_BADGE: "Paid",
    SLEEVE_LENGTH: "Sleeve Length",
  },
  BUTTONS: {
    MARK_AS_PAID: "Mark As Paid",
    MARKING_AS_PAID: "Marking as Paid...",
  },
  TASK_CARD: {
    TITLE: "Partner kit update",
    URL: "", // Will be set dynamically using getOnboardingTasksLink
  },
  ERRORS: {
    ORDER_ID_MISSING: {
      TITLE: "Error",
      DESCRIPTION: "Order ID is missing",
    },
    MARK_PAID_FAILED: {
      TITLE: "Error",
      DESCRIPTION: "Failed to mark order as paid. Please try again.",
    },
  },
  SUCCESS: {
    ORDER_MARKED_PAID: {
      TITLE: "Success",
      DESCRIPTION: "Order marked as paid successfully",
    },
  },
} as const;

// Overview Tab Constants
export const LOADING_PERFORMANCE_MESSAGE = "Loading performance data...";
export const NO_MONTHLY_TARGETS_MESSAGE = "No monthly targets set yet";
export const EMPTY_STRING = "";

// Reviews Tab Constants
export const REVIEWS_PER_PAGE = 10;
export const MAX_VISIBLE_PAGES = 5;
export const DEFAULT_RATING_DISPLAY = "0.0";
export const LOADING_REVIEWS_MESSAGE = "Loading reviews...";
export const NO_REVIEWS_MESSAGE = "No reviews yet";
export const REVIEW_LABEL_SINGULAR = "review";
export const REVIEW_LABEL_PLURAL = "reviews";
export const PAGINATION_ELLIPSIS = "...";
export const PAGINATION_PREVIOUS = "Previous";
export const PAGINATION_NEXT = "Next";

// Working Hours Tab Constants
export const LEAVES_PER_PAGE = 10;
export const MAX_UPCOMING_LEAVES = 5;
export const LOADING_WORKING_HOURS_MESSAGE = "Loading working hours...";
export const ERROR_LOADING_WORKING_HOURS_MESSAGE =
  "Error loading working hours. Please try again.";
export const NO_WORKING_HOURS_MESSAGE = "No working hours data available";
export const NO_WORKING_DAYS_MESSAGE = "No working days set";
export const NO_TIME_SLOTS_MESSAGE = "No time slots set";
export const LOADING_LEAVES_MESSAGE = "Loading leaves...";
export const NO_UPCOMING_LEAVES_MESSAGE = "No upcoming leaves";

// Partner Profile Page Constants
export const LOADING_PARTNER_DATA_MESSAGE = "Loading partner data...";
export const PARTNER_NOT_FOUND_MESSAGE = "Partner not found";
export const DEFAULT_SUB_TAB = EPartnerProfileTabId.OVERVIEW;

// Assignment Details Modal Constants
export const ASSIGNMENT_MODAL_CONTENT = {
  UPDATE: {
    TITLE: "Assign your partner",
    DESCRIPTION: (partnerName: string) =>
      `Assign subcity, city, hub and hotspot to activate ${partnerName} as an active partner`,
    BUTTON_LABEL: "Update",
    BUTTON_LOADING_LABEL: "Updating...",
  },
  ASSIGN: {
    TITLE: "Assign Location to Partner",
    DESCRIPTION: (partnerName: string) =>
      `Assign location details to ${partnerName} before marking them as active`,
    BUTTON_LABEL: "Assign",
    BUTTON_LOADING_LABEL: "Assigning...",
  },
} as const;
